import { FC, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Box, Button, Stack, Text, Title } from '@mantine/core';
import { useOktaAuth } from '@okta/okta-react';
import * as Sentry from '@sentry/react';

import logger from '@/utils/Logger';
import { LogoIcon } from '@/assets/icons';

const EPIC_LAUNCH_ERROR_MESSAGES: Record<string, string> = {
  expired_or_invalid_state: 'Your Epic session expired before login could complete.',
  organization_not_found: 'This Epic organization is not configured for Rhythm.',
  token_exchange_failed: 'We were unable to complete sign-in with Epic.',
  practitioner_lookup_failed: 'We were unable to verify your Epic provider record.',
  user_not_found:
    'We could not find a Rhythm account linked to your Epic provider record. Please contact your administrator.'
};

// Landing point for the backend's Epic SMART-on-FHIR EHR launch redirect (see synergy-api
// src/modules/epic-launch/). The backend has already exchanged the Epic launch code for a
// token server-side; this page's only job is to hand off into the existing Okta login flow
// (silent SSO if a session exists, otherwise Okta's normal prompt).
const EPIC_IDP_ID = import.meta.env.VITE_OKTA_EPIC_IDP_ID;

const EpicLoginHandoffPage: FC = () => {
  const { oktaAuth } = useOktaAuth();
  const [searchParams] = useSearchParams();
  const epicLaunchError = searchParams.get('epicLaunchError');
  const loginHint = searchParams.get('loginHint');
  const hasRedirected = useRef(false);
  const [redirectFailed, setRedirectFailed] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const errorMessage = epicLaunchError
    ? Object.prototype.hasOwnProperty.call(EPIC_LAUNCH_ERROR_MESSAGES, epicLaunchError)
      ? EPIC_LAUNCH_ERROR_MESSAGES[epicLaunchError]
      : 'Something went wrong launching from Epic.'
    : 'We were unable to sign you in. Please try again.';

  const continueToLogin = () => {
    if (hasRedirected.current) return;
    hasRedirected.current = true;
    setRedirectFailed(false);
    setIsRedirecting(true);
    oktaAuth.setOriginalUri('/');
    if (loginHint && !EPIC_IDP_ID) {
      const message =
        'Epic handoff: VITE_OKTA_EPIC_IDP_ID is not configured; falling back to standard login';
      logger.warn(message);
      Sentry.captureMessage(message, 'warning');
    }
    // prompt: 'login' forces Okta to re-authenticate even if a session already exists in this
    // browser (e.g. a shared clinic workstation) — otherwise a stale session for a different
    // user could silently satisfy this Epic launch and return the wrong user's tokens.
    //
    // idp is only included alongside a loginHint: it routes Okta straight to the Epic IdP
    // federation flow, which requires the SMART launch context we no longer have once the
    // backend has already failed the launch. Sending idp without a loginHint (the failure
    // path) sent users into that dead-end flow instead of the normal Okta login screen.
    const params = {
      prompt: 'login',
      ...(EPIC_IDP_ID && loginHint ? { idp: EPIC_IDP_ID } : {}),
      ...(loginHint ? { loginHint } : {})
    };
    oktaAuth.signInWithRedirect(params).catch((error) => {
      hasRedirected.current = false; // allow retry
      logger.error('Epic handoff: signInWithRedirect failed', error as Error);
      Sentry.captureException(error);
      setRedirectFailed(true);
      setIsRedirecting(false);
    });
  };

  useEffect(() => {
    // loginHint carries PII (the clinician's Epic username/email) in the query string, which
    // would otherwise persist in browser history and get picked up by Sentry replay/tracing.
    // Strip it from the visible URL immediately; the value has already been read above.
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (epicLaunchError) {
      logger.error(`Epic launch failed: ${epicLaunchError}`);
      Sentry.captureMessage(`Epic launch failed: ${epicLaunchError}`, {
        level: 'error',
        tags: { epicLaunchError }
      });
      return;
    }
    continueToLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epicLaunchError, loginHint]);

  if (!epicLaunchError && !redirectFailed) {
    return (
      <Stack w={'100%'} justify={'center'} align={'center'} mih={'90vh'} gap={'md'}>
        <Box mb={2}>
          <LogoIcon width={160} height={160} />
        </Box>
        <Title c={'gray.8'} size={'h4'} fw={'600'} lh={0}>
          Signing you in
        </Title>
        <Text c={'gray.6'}>Completing your Epic launch...</Text>
      </Stack>
    );
  }

  return (
    <Stack w={'100%'} justify={'center'} align={'center'} mih={'90vh'} gap={'md'}>
      <Box mb={2}>
        <LogoIcon width={160} height={160} />
      </Box>
      <Title c={'gray.8'} size={'h4'} fw={'600'} lh={0}>
        Epic Launch Failed
      </Title>
      <Text c={'gray.6'}>{errorMessage}</Text>
      <Button onClick={continueToLogin} loading={isRedirecting}>
        Continue to Login
      </Button>
    </Stack>
  );
};

export default EpicLoginHandoffPage;
